import { BigNumber } from '@ethersproject/bignumber';
import {
  getChecksumAddressOrThrow,
  SiloData,
  SiloDataAsset,
  USD_DECIMALS,
} from '@silo/types';
import { getConfigDepositMode } from '@silo/config';
import { SiloConfiguration } from '@silo/config-legacy';
import { MerklCampaign } from '@silo/data';
import { BIG_ZERO } from 'app/constants';
import { DecoratedSilo, SiloAsset } from './types';
import { toLendingMerklRewardLegacy } from './to-lending-merkl-reward-legacy';

interface ToDecoratedSiloParams {
  siloData: SiloData;
  siloConfig: SiloConfiguration;
  merklCampaigns: MerklCampaign[];
}

export function toDecoratedSilo({
  siloData,
  siloConfig,
  merklCampaigns,
}: ToDecoratedSiloParams): DecoratedSilo {
  const {
    protocolKey,
    marketAddress,
    routerAddress,
    rewardTokenPriceUsd,
    rewardTokenDecimals,
    assets,
    userLtv,
    userMaxLtv,
    userLiquidationThreshold,
    hasPosition,
  } = siloData.data;

  const siloAddress = getChecksumAddressOrThrow(marketAddress);
  const nameOverride = siloConfig.name_MUST_SYNCHRONIZE;
  const symbolOverride = siloConfig.symbol_MUST_SYNCHRONIZE;

  const decoratedSiloAssets = assets.map((asset, i) => {
    const isBaseAsset = i === 0;

    const assetMerklCampaigns = merklCampaigns.filter(
      (campaign) => campaign.tokenAddress === asset.tokenAddress
    );

    return toSiloAsset({
      asset,
      isBaseAsset,
      nameOverride,
      symbolOverride,
      protocolKey,
      marketAddress,
      assetMerklCampaigns,
      rewardTokenPriceUsd,
      rewardTokenDecimals,
    });
  });

  const decoratedSilo: DecoratedSilo = {
    siloAddress,
    assets: decoratedSiloAssets,
    userLTV: bnFromBigint(userLtv),
    userMaximumLTV: bnFromBigint(userMaxLtv),
    userLiquidationThreshold: bnFromBigint(userLiquidationThreshold),
    hasPosition,
    routerAddress: getChecksumAddressOrThrow(routerAddress),
  };

  return decoratedSilo;
}

interface ToSiloAssetParams {
  asset: SiloDataAsset;
  isBaseAsset: boolean;
  nameOverride: string | undefined;
  symbolOverride: string | undefined;
  protocolKey: SiloData['data']['protocolKey'];
  marketAddress: SiloData['data']['marketAddress'];
  assetMerklCampaigns: MerklCampaign[];
  rewardTokenPriceUsd: bigint;
  rewardTokenDecimals: number;
}

function toSiloAsset({
  asset,
  isBaseAsset,
  nameOverride,
  symbolOverride,
  protocolKey,
  marketAddress,
  assetMerklCampaigns,
  rewardTokenPriceUsd,
  rewardTokenDecimals,
}: ToSiloAssetParams): SiloAsset {
  const assetAddress = getChecksumAddressOrThrow(asset.tokenAddress);
  const userBalanceTokenAddress = getChecksumAddressOrThrow(asset.effectiveTokenAddress);
  const priceProviders = getChecksumAddressOrThrow(asset.priceProvider);

  const name = (isBaseAsset && nameOverride) || asset.name;
  const symbol = (isBaseAsset && symbolOverride) || asset.symbol;

  const depositMode = getConfigDepositMode({
    protocolKey,
    marketAddress,
    assetKey: asset.assetKey,
  });
  const isProtected = depositMode === 'protectedOnly' || depositMode === 'defaultFalse';

  const lendingMerklRewards = assetMerklCampaigns.map((campaign) =>
    toLendingMerklRewardLegacy({
      campaign,
      totalCollateral: asset.collateralTotal,
      totalProtected: asset.protectedTotal,
      totalDebt: asset.debtTotal,
      symbol: asset.symbol,
      decimals: asset.decimals,
      tokenPriceUsd: { value: asset.priceUsd, decimals: USD_DECIMALS },
    })
  );

  const depositRewardsApr = isProtected
    ? bnFromBigint(asset.protectedRewardsApr)
    : bnFromBigint(asset.collateralRewardsApr);

  return {
    assetAddress,
    assetKey: asset.assetKey,
    totalDeposits: bnFromBigint(asset.collateralTotal),
    collateralOnlyDeposits: bnFromBigint(asset.protectedTotal),
    totalBorrowAmount: bnFromBigint(asset.debtTotal),
    liquidity: bnFromBigint(asset.liquidity),
    utilization: bnFromBigint(asset.utilization),
    symbol,
    rawSymbol: asset.rawSymbol,
    name,
    decimals: asset.decimals,
    isNative: asset.isNative,
    depositAPY: bnFromBigint(asset.collateralBaseApr),
    collateralRewardsApr: bnFromBigint(asset.collateralRewardsApr),
    protectedRewardsApr: bnFromBigint(asset.protectedRewardsApr),
    depositRewardsApr,
    borrowAPY: bnFromBigint(asset.debtBaseApr),
    borrowRewardsApr: bnFromBigint(asset.debtRewardsApr),
    userBalanceTokenAddress,
    userBalance: bnFromBigint(asset.tokenBalance),
    collateralBalance: bnFromBigint(asset.collateralBalance),
    collateralOnlyBalance: bnFromBigint(asset.protectedBalance),
    debtBalance: bnFromBigint(asset.debtBalance),
    projectedDebtBalance: bnFromBigint(asset.projectedDebtBalance),
    priceInNative: bnFromBigint(asset.priceNative),
    priceProviders,
    maximumLTV: bnFromBigint(asset.maxLtv),
    liquidationThreshold: bnFromBigint(asset.lt),
    isCurveLpAsset: asset.isCurveLpAsset,
    lendingMerklRewards,

    collateralTotal: asset.collateralTotal,
    collateralEmissionPerSecond: asset.collateralEmissionPerSecond,
    protectedTotal: asset.protectedTotal,
    protectedEmissionPerSecond: asset.protectedEmissionPerSecond,
    debtTotal: asset.debtTotal,
    debtEmissionPerSecond: asset.debtEmissionPerSecond,
    rewardTokenPriceUsd,
    rewardTokenDecimals,
    tokenPriceUsd: asset.priceUsd,
  };
}

function bnFromBigint(value: bigint): BigNumber {
  return value === 0n ? BIG_ZERO : BigNumber.from(value);
}
